/**
 * Sonda do cache do service worker
 *
 * ## A pergunta que a telemetria de hoje NÃO responde
 *
 * Sabemos a release do BUNDLE que está rodando (`client_code_version`,
 * `BUILD_INFO.release`). Não sabemos a release do SERVICE WORKER que serve esse
 * device — e é ela que decide se o fóssil ainda existe ali.
 *
 * Medição de 15/08/2026 (devices distintos com `ChunkLoadError` no boot, dia
 * inteiro): a build `v3-hybrid` está drenando — 397 → 276 → 272 → 234 → 219
 * entre 10 e 14/08 — e não pode ser reposta, porque o `CLIENT_CODE_VERSION`
 * deixou de ser `v3-hybrid` em 27/07. Quem volta, volta curado: dos fósseis
 * que reaparecem no dia seguinte, 90% já rodam v5.
 *
 * O resíduo está noutro lugar: **128 devices REGREDIRAM** — rodaram v5 num dia
 * e erraram no boot com `v4-ng18` num dia posterior. Isto é o que a régua atual
 * não consegue explicar, porque rodar a build nova pela rede **não prova** que
 * o service worker foi trocado. Um device pode carregar v5 da rede e continuar
 * com o worker antigo registrado e o cache legado armado; na próxima falha de
 * rede, o `.catch()` daquele worker serve o index velho de novo.
 *
 * ## Por que o cache é a régua certa
 *
 * O `servicePwaWorker.js` nomeia seu cache `pedir-delivery-pwa-<release>` desde
 * a Etapa 1 (10/08). Antes disso o nome era **constante e sem sufixo**. Então o
 * nome do cache é uma assinatura direta e observável de qual worker chegou a
 * INSTALAR naquele device:
 *
 *  - `pedir-delivery-pwa` (sem sufixo) presente ⇒ o worker pré-Etapa-1 instalou
 *    ali e o `activate` do worker novo **nunca rodou** para podá-lo. É o device
 *    que ainda pode servir um index fóssil.
 *  - `pedir-delivery-pwa-<sha>` ⇒ qual release do worker ativou por último.
 *    Comparado com `BUILD_INFO.release` no Athena, diz se o worker ficou para
 *    trás do bundle.
 *  - mais de um cache com o prefixo ⇒ a poda do `activate` não completou.
 *
 * Isto é preferível a perguntar ao worker por `postMessage`: o worker ANTIGO —
 * exatamente o que queremos contar — não tem handler de mensagem e nunca
 * responderia. O `CacheStorage` é legível pela página sem cooperação nenhuma
 * dele.
 *
 * ## Por que sondar no boot e ler síncrono depois
 *
 * `caches.keys()` é assíncrono e o `ClientAppError` é montado dentro do
 * ErrorHandler global, que é síncrono. A sonda roda uma vez no boot e memoiza;
 * o diagnóstico só lê o que já está em memória.
 *
 * Consequência deliberada, no mesmo padrão do `retry_n` do `chunk-retry.ts`: se
 * o erro acontecer ANTES de a sonda terminar, os campos ficam **ausentes** em
 * vez de mentir um valor. Ausência é informação honesta ("não deu tempo");
 * um `false` inventado viraria "não tem cache legado" e enviesaria exatamente a
 * contagem que este módulo existe para produzir.
 */

/**
 * Prefixo dos caches do PWA. **Tem de casar com `CACHE_PREFIX` do
 * `src/servicePwaWorker.js`** — é por ele que o `activate` poda e é por ele que
 * esta sonda conta. Se os dois divergirem, a sonda para de enxergar os caches
 * reais e passa a reportar "nenhum" em silêncio.
 */
export const PREFIXO_CACHE = 'pedir-delivery-pwa';

/** Campos que a sonda acrescenta ao diagnóstico. Todos opcionais. */
export interface EstadoCacheSw {
  /**
   * O cache LEGADO (nome sem sufixo de release) está presente. `true` significa
   * que o service worker anterior à Etapa 1 instalou neste device e o
   * `activate` do worker novo nunca rodou para podá-lo — o device que ainda
   * pode bootar de um index fóssil.
   */
  sw_cache_legacy?: boolean;
  /**
   * Release do worker que ativou por último (sufixo do nome do cache). Cruzada
   * com `BUILD_INFO.release` no Athena, mede a distância entre o worker e o
   * bundle. Ausente quando só existe o cache legado ou nenhum.
   */
  sw_cache_release?: string;
  /**
   * Quantos caches com o prefixo existem. `> 1` é anomalia: a poda do
   * `activate` não completou, e o `.catch()` de rede passa a ter mais de um
   * candidato a servir o documento.
   */
  sw_cache_n?: number;
}

/** Teto do sufixo no payload — a release é um sha curto; o corte é só defesa. */
const MAX_RELEASE = 40;

/** Resultado memoizado. `null` = a sonda ainda não concluiu (ou não roda aqui). */
let estado: EstadoCacheSw | null = null;

/** Evita sondar duas vezes se o boot chamar mais de uma vez. */
let sondando = false;

/**
 * Estado do cache do service worker, para o diagnóstico consumir. Devolve `{}`
 * enquanto a sonda não concluiu — nunca um valor inventado (ver cabeçalho).
 */
export function estadoDoCacheDoSw(): EstadoCacheSw {
  return estado || {};
}

/**
 * Deriva o estado a partir dos nomes de cache. Separada da leitura assíncrona
 * de propósito: é a regra de negócio inteira do módulo e o teste a exercita
 * sem `CacheStorage`, sem browser e sem Promise.
 */
export function derivarEstado(nomes: string[]): EstadoCacheSw {
  const doPwa = (nomes || []).filter(
    n => typeof n === 'string' && n.indexOf(PREFIXO_CACHE) === 0,
  );
  const e: EstadoCacheSw = { sw_cache_n: doPwa.length };

  // O nome EXATO, sem sufixo, é a assinatura do worker pré-Etapa-1.
  e.sw_cache_legacy = doPwa.indexOf(PREFIXO_CACHE) !== -1;

  // `-` a mais no slice: o sufixo é `<prefixo>-<release>`.
  const versionados = doPwa
    .filter(n => n.length > PREFIXO_CACHE.length + 1)
    .map(n => n.slice(PREFIXO_CACHE.length + 1));
  if (versionados.length) e.sw_cache_release = versionados[0].slice(0, MAX_RELEASE);

  return e;
}

/**
 * Lê o `CacheStorage` uma vez e memoiza. Fire-and-forget: nunca lança, nunca
 * rejeita e não atrasa o boot — se o contexto não tiver `caches` (http sem TLS,
 * webview restrita, teste), simplesmente não memoiza nada e os campos seguem
 * ausentes.
 *
 * `cacheStorage` existe para o teste; o padrão é o do browser.
 */
export function sondarCacheDoSw(
  cacheStorage: { keys(): Promise<string[]> } | null =
    (typeof caches === 'undefined' ? null : caches),
): Promise<void> {
  try {
    if (sondando || estado || !cacheStorage || typeof cacheStorage.keys !== 'function') {
      return Promise.resolve();
    }
    sondando = true;
    return cacheStorage.keys()
      .then(nomes => { estado = derivarEstado(nomes); })
      // Falhou (storage particionado, quota, contexto inseguro): campos
      // ausentes. Uma sonda de medição não pode virar o segundo erro.
      .catch(() => undefined)
      .then(() => { sondando = false; });
  } catch (e) {
    sondando = false;
    return Promise.resolve();
  }
}

/** Só para teste: devolve o módulo ao estado de antes de sondar. */
export function _resetarSondaParaTeste(): void {
  estado = null;
  sondando = false;
}
